import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { Check, PartyPopper, X } from "lucide-react";
import { type QuizDetail } from "~/api/types";
import { completeQuiz } from "~/api/v2/requests";
import { QuizReason } from "~/molecules/QuizReason";
import { Button } from "~/atoms/button";
import { Progress } from "~/atoms/progress";
import { cn } from "~/lib/utils";

interface Props {
  quiz: QuizDetail;
  onCompleted: () => void;
}

/**
 * Take a quiz question by question. The taker must select the correct answer to
 * advance; the answer options carry the correct flag, so correctness is checked
 * locally. Finishing the last question marks the quiz completed for the user.
 */
const QuizTaker = ({ quiz, onCompleted }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [questionIndex, setQuestionIndex] = useState(0);
  const [answeredCorrectly, setAnsweredCorrectly] = useState(false);
  const [wrongTried, setWrongTried] = useState<Set<number>>(new Set());
  const [isFinishing, setIsFinishing] = useState(false);
  const [done, setDone] = useState(false);

  const total = quiz.questions.length;
  const question = quiz.questions[questionIndex];
  const isLast = questionIndex === total - 1;

  if (!question) return null;

  const selectOption = (index: number) => {
    if (answeredCorrectly || wrongTried.has(index)) return;
    if (question.options[index]?.isCorrect) {
      setAnsweredCorrectly(true);
    } else {
      setWrongTried((prev) => new Set(prev).add(index));
    }
  };

  const goNext = () => {
    setQuestionIndex((index) => index + 1);
    setAnsweredCorrectly(false);
    setWrongTried(new Set());
  };

  const finish = async () => {
    setIsFinishing(true);
    try {
      await completeQuiz(quiz.id);
      onCompleted();
      setDone(true);
    } catch {
      toast.error(t(($) => $.anchoring.quiz.take.error));
    } finally {
      setIsFinishing(false);
    }
  };

  if (done) {
    return (
      <div className="flex justify-center">
        <div className="flex max-w-lg flex-col items-center gap-4 py-10 text-center">
          <PartyPopper className="text-primary size-12" />
          <h2 className="text-xl font-semibold">
            {t(($) => $.anchoring.quiz.take.completedTitle)}
          </h2>
          <p className="text-muted-foreground">
            {t(($) => $.anchoring.quiz.take.completedDescription)}
          </p>
          <Button onClick={() => void navigate("/anchoring/me")}>
            {t(($) => $.anchoring.quiz.take.backToList)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <div className="flex w-full max-w-2xl flex-col gap-6">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm">
            {t(($) => $.anchoring.quiz.take.progress, {
              current: questionIndex + 1,
              total,
            })}
          </p>
          <Progress value={(questionIndex / total) * 100} />
        </div>

        <h2 className="text-lg font-semibold">{question.question}</h2>

        <div className="flex flex-col gap-2">
          {question.options.map((option, index) => {
            const isWrong = wrongTried.has(index);
            const isCorrect = answeredCorrectly && option.isCorrect;
            return (
              <button
                key={index}
                type="button"
                onClick={() => selectOption(index)}
                disabled={answeredCorrectly || isWrong}
                className={cn(
                  "flex items-center justify-between rounded-lg border px-4 py-3 text-left text-sm transition-colors",
                  "enabled:hover:border-primary enabled:hover:bg-muted disabled:cursor-not-allowed",
                  isCorrect && "border-success text-success bg-success/10",
                  isWrong &&
                    "border-destructive text-destructive bg-destructive/10",
                )}
              >
                <span>{option.text}</span>
                {isCorrect && <Check className="size-4 shrink-0" />}
                {isWrong && <X className="size-4 shrink-0" />}
              </button>
            );
          })}
        </div>

        {answeredCorrectly && question.reason && (
          <div className="border-success bg-success/10 flex flex-col gap-2 rounded-lg border p-4">
            <p className="text-success text-sm font-medium">
              {t(($) => $.anchoring.quiz.take.reasonHeading)}
            </p>
            <QuizReason html={question.reason} className="text-foreground" />
          </div>
        )}

        {wrongTried.size > 0 && !answeredCorrectly && (
          <p className="text-destructive text-sm">
            {t(($) => $.anchoring.quiz.take.incorrect)}
          </p>
        )}

        <div className="flex justify-end">
          {isLast ? (
            <Button
              onClick={() => void finish()}
              disabled={!answeredCorrectly || isFinishing}
            >
              {t(($) => $.anchoring.quiz.take.finish)}
            </Button>
          ) : (
            <Button onClick={goNext} disabled={!answeredCorrectly}>
              {t(($) => $.anchoring.quiz.take.next)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizTaker;
